import { redactStack, redactText, safeErrorName, type ClientErrorReport } from '@wodi/shared';

import { ApiError } from '../api/client.ts';

// Error tracking in the browser without patient data (ADR 0007). A report carries the
// error's class, its redacted message and stack frames, and the route pattern. Never
// the address bar, query string, form values, page text or component state. The
// server redacts the report again when it arrives.

type Kind = ClientErrorReport['kind'];

export interface ErrorReporter {
  report(error: unknown, kind: Kind): void;
}

export interface ErrorReporterOptions {
  send: (report: ClientErrorReport) => Promise<unknown>;
  /** The current route pattern, e.g. /patients/$patientId. */
  getRoute: () => string | undefined;
  /** At most this many reports per page load, so a render loop can't flood the server. */
  maxReports?: number;
}

const ROUTE_PATTERN = /^\/[A-Za-z0-9/_$.-]{0,199}$/;

function field(value: unknown, key: string): unknown {
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

/** Builds the report for an error, or null if it shouldn't be reported. */
export function buildReport(
  error: unknown,
  kind: Kind,
  route: string | undefined,
): ClientErrorReport | null {
  // API failures are already recorded by the server (with the reference the person
  // sees), and offline is not a bug.
  if (error instanceof ApiError) return null;
  const message = field(error, 'message');
  const stack = field(error, 'stack');
  const redactedStack = redactStack(typeof stack === 'string' ? stack : undefined);
  const report: ClientErrorReport = {
    kind,
    name: safeErrorName(field(error, 'name')),
    message: redactText(typeof message === 'string' ? message : ''),
    route: route !== undefined && ROUTE_PATTERN.test(route) ? route : '/',
  };
  if (redactedStack !== null) report.stack = redactedStack;
  return report;
}

export function createErrorReporter(options: ErrorReporterOptions): ErrorReporter {
  const max = options.maxReports ?? 10;
  const seen = new Set<string>();
  return {
    report(error, kind) {
      if (seen.size >= max) return;
      const report = buildReport(error, kind, options.getRoute());
      if (report === null) return;
      const key = [report.name, report.message, report.stack?.split('\n')[0] ?? ''].join('|');
      if (seen.has(key)) return;
      seen.add(key);
      // Reporting must never cause another error: failures are dropped.
      options.send(report).catch(() => undefined);
    },
  };
}

/** Reports errors that escape everything else: uncaught exceptions and rejected promises. */
export function installGlobalErrorHandlers(
  target: Pick<Window, 'addEventListener'>,
  reporter: ErrorReporter,
): void {
  target.addEventListener('error', (event: ErrorEvent) => {
    // Scripts from another origin give no detail; there is nothing useful to send.
    if (event.error === null || event.error === undefined) return;
    reporter.report(event.error, 'error');
  });
  target.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reporter.report(event.reason, 'unhandledrejection');
  });
}
